import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useAppLayout } from '@/components/AppLayout/AppLayoutProvider'
import { safePlay } from '@/utils/media.utils'
import AudioEngine from '@/webgl/audio/AudioEngine'

import StandaloneSubtitles from './StandaloneSubtitles'

// import Subtitles from "./Subtitles";

// TODO REWORK THIS COMPONENT USING useAudioSubtitles
export type VideoAndSubtitlesProps = {
  sources: {
    mp4?: string
    webm?: string
  }
  subtitlesSrc: string
  onEnded?: () => void
  onLoaded?: () => void
  onPlay?: () => void
  onPause?: () => void
}

export type VideoAndSubtitlesRef = {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>
}

const VideoAndSubtitles = forwardRef<VideoAndSubtitlesRef, VideoAndSubtitlesProps>(
  ({ sources, subtitlesSrc, onEnded, onLoaded, onPause, onPlay }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const [time, setTime] = useState<number | null>(null)

    const { footerCenter } = useAppLayout()

    const onTimeUpdate = useCallback(() => {
      if (videoRef.current) {
        setTime(videoRef.current.currentTime)

        if (videoRef.current.currentTime >= videoRef.current.duration - 1) {
          onEnded?.()
        }
      }
    }, [videoRef, setTime, onEnded])

    const [hasPlayed, setHasPlayed] = useState(false)
    // FORCE AUTOPLAY ON FIRST CLICK IF NOT PLAYING
    useEffect(() => {
      const onDocumentClick = () => {
        if (videoRef.current && videoRef.current.paused && !hasPlayed) {
          safePlay(videoRef.current)
          setHasPlayed(true)
          document.removeEventListener('click', onDocumentClick)
        }
      }
      document.addEventListener('click', onDocumentClick)

      return () => {
        document.removeEventListener('click', onDocumentClick)
      }
    }, [videoRef, hasPlayed, setHasPlayed])

    const volume = AudioEngine.instance.master.gain.value
    useLayoutEffect(() => {
      if (videoRef.current) {
        videoRef.current.volume = volume
      }
    }, [volume, videoRef])

    const [loaded, setLoaded] = useState(false)
    const onPlaying = useCallback(() => {
      if (!loaded) {
        setLoaded(true)
        onLoaded?.()
      }
    }, [loaded, setLoaded, onLoaded])

    useEffect(() => {
      const video = videoRef.current

      const handlePlay = () => {
        removeListeners()
        if (!loaded) {
          setLoaded(true)
          onLoaded?.()
        }
      }

      const addListeners = () => {
        video?.addEventListener('loadedmetadata', handlePlay)
        video?.addEventListener('canplay', handlePlay)
        video?.addEventListener('playing', handlePlay)
      }

      const removeListeners = () => {
        video?.removeEventListener('loadedmetadata', handlePlay)
        video?.removeEventListener('canplay', handlePlay)
        video?.removeEventListener('playing', handlePlay)
      }

      addListeners()

      return () => {
        removeListeners()
      }
    }, [loaded, setLoaded, onLoaded])

    useImperativeHandle(ref, () => {
      return {
        videoRef,
      }
    })

    return (
      <>
        <video
          ref={videoRef}
          playsInline
          autoPlay
          onTimeUpdate={onTimeUpdate}
          className='max-md:fixed md:absolute z-2 top-0 left-0 w-full h-full object-cover'
          preload='auto'
          loop={false}
          onPlay={() => {
            setHasPlayed(true)
            onPlay?.()
          }}
          onPause={onPause}
          // onPlaying={onPlaying}
        >
          {sources.mp4 && <source src={sources.mp4} type='audio/mp4' />}
          {sources.webm && <source src={sources.webm} type='audio/webm' />}

          <track src={subtitlesSrc}></track>
        </video>
        {videoRef.current && (
          <div
            className={`max-md:fixed md:absolute z-2 inset-0 size-full pointer-events-none bg-cream/30 transition-opacity duration-333 ease-out-quad ${videoRef.current.paused ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        {videoRef.current && (
          <div
            className={`max-md:fixed md:absolute z-2 inset-x-0 bottom-0 h-2/3 w-full pointer-events-none bg-linear-to-t from-cream to-cream/0 transition-opacity duration-333 ease-out-quad ${videoRef.current.paused ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        {footerCenter.current &&
          createPortal(<StandaloneSubtitles time={time} src={subtitlesSrc} />, footerCenter.current)}
      </>
    )
  }
)

export default VideoAndSubtitles
